/**
 * Quantity Selector custom element.
 * Preset buttons (Single/Pair/Dual/Triplet) that set the quantity.
 * Updates the main product price display and syncs with the +/- quantity input.
 */
if (!customElements.get('quantity-selector')) {
  class QuantitySelector extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('.js-qty-option');
      this.mode = this.dataset.mode;
      this.sectionId = this.dataset.sectionId;
      this.unitPrice = parseInt(this.dataset.unitPrice, 10);
      this.compareAtPrice = 0;
      this.selectedQty = 1;
    }

    connectedCallback() {
      // Cache DOM references once so click handlers do zero lookups
      this.priceBlock = document.querySelector('[data-dynamic-variant-content="price"]');
      this.currentPriceEl = this.priceBlock?.querySelector('.price__current .js-value');
      this.comparePriceEl = this.priceBlock?.querySelector('.price__was .js-value');
      this.form = this.closest('[data-section-id]')?.querySelector('.js-product-form')
        || document.querySelector('.js-product-form');
      this.qtyInputs = this.form ? this.form.querySelectorAll('input[name="quantity"]') : [];
      this.qtyComponent = this.form?.querySelector('quantity-input input[type="number"]');

      this.buttons.forEach((btn) => {
        btn.addEventListener('change', this.handleChange.bind(this));
      });

      document.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      this.observeQuantityInput();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.handleVariantChange.bind(this));
    }

    observeQuantityInput() {
      if (!this.qtyComponent) return;

      this.qtyComponent.addEventListener('change', () => {
        const newQty = parseInt(this.qtyComponent.value, 10);
        if (isNaN(newQty) || newQty < 1) return;

        this.selectedQty = newQty;
        this.updateMainPrice();

        this.buttons.forEach((btn) => {
          btn.checked = parseInt(btn.value, 10) === newQty;
        });
      });
    }

    handleChange(event) {
      const qty = parseInt(event.target.value, 10);
      this.selectedQty = qty;
      this.setQuantity(qty);
      this.updateMainPrice();
    }

    handleVariantChange(event) {
      if (!event.detail || !event.detail.variant) return;

      const variant = event.detail.variant;
      this.unitPrice = variant.price;
      this.compareAtPrice = variant.compare_at_price || 0;
      this.dataset.unitPrice = variant.price;

      // Variant picker replaces the price DOM, so re-query after a tick
      requestAnimationFrame(() => {
        this.priceBlock = document.querySelector('[data-dynamic-variant-content="price"]');
        this.currentPriceEl = this.priceBlock?.querySelector('.price__current .js-value');
        this.comparePriceEl = this.priceBlock?.querySelector('.price__was .js-value');
        if (this.selectedQty > 1) this.updateMainPrice();
      });
    }

    setQuantity(qty) {
      this.qtyInputs.forEach((input) => {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (this.qtyComponent) {
        this.qtyComponent.value = qty;
        this.qtyComponent.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.dispatchEvent(new CustomEvent('on:quantity-selector:change', {
        bubbles: true,
        detail: { quantity: qty, unitPrice: this.unitPrice, totalPrice: this.unitPrice * qty }
      }));
    }

    updateMainPrice() {
      if (!this.currentPriceEl) return;

      this.currentPriceEl.textContent = this.formatMoney(this.unitPrice * this.selectedQty);

      if (this.compareAtPrice > 0 && this.comparePriceEl) {
        this.comparePriceEl.innerHTML = '<s>' + this.formatMoney(this.compareAtPrice * this.selectedQty) + '</s>';
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts.join('.');
    }
  }

  customElements.define('quantity-selector', QuantitySelector);
}
